'use client';

import { Button } from '@/components/ui/button';
import { logout } from '@/services/auth';
import { LogOut } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

/**
 * Onboarding intentionally hides the normal application navigation. Keep a
 * session escape hatch visible so users can always switch accounts without
 * clearing browser data or opening developer tools.
 */
export default function OnboardingSignOut() {
  const pathname = usePathname();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const isOnboarding = pathname === '/onboarding' || pathname.startsWith('/onboarding/');

  if (!isOnboarding) return null;

  const handleSignOut = async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    try {
      await logout();
    } finally {
      // Normally the logout helper navigates away. Restore the control if a
      // browser blocks that navigation so the user is never left with a dead UI.
      setIsSigningOut(false);
    }
  };

  return (
    <div className="fixed right-3 top-3 z-[100] sm:right-5 sm:top-5">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="border-slate-300 bg-white/95 text-slate-700 shadow-sm backdrop-blur hover:bg-white hover:text-slate-950"
        startIcon={<LogOut aria-hidden="true" className="h-4 w-4" />}
        isLoading={isSigningOut}
        disabled={isSigningOut}
        aria-label="Sign out and switch accounts"
        onClick={handleSignOut}
      >
        {isSigningOut ? 'Signing out…' : 'Sign out'}
      </Button>
    </div>
  );
}
